<!--
CreatePurchaseOrder Component - Purchase Order Creation
====================================================

Features:
1. Brand selection with product search functionality
2. Product selection with automatic packaging assignment
3. Quantity and remark input for each product
4. Date picker for received date
5. Auto packaging selection based on product.original_price_unit

API Changes:
- Each product item includes:
  - packaging_selection: string (auto-selected based on product.original_price_unit or defaults to 'single_item')

Maintenance Notes:
- No FOC functionality (simplified version of AddDirectPurchase)
- Uses /bd/purchase-order/create API endpoint
- Clean, straightforward Purchase Order creation process
-->
<template>
	<div class="mainWrapper direct-purchase">
		<h1>Create Purchase Order</h1>
		<div class="form" v-if="suppliers.length > 0">
			<div>
				<span class="label">SUPPLIER</span>
				<b-autocomplete
					v-model="supplierSuggestions"
					:data="filteredSuppliers"
					:open-on-focus="true"
					placeholder="Supplier"
					field="name"
					@select="option => (selectedSupplier = option)"
					class="brandAutocomplete"
					:disabled="products.length > 0"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</div>

			<div v-if="selectedSupplier && brands.length > 0">
				<span class="label">BRAND</span>
				<b-autocomplete
					v-model="brandSuggestions"
					:data="filteredBrands"
					:open-on-focus="true"
					placeholder="Brand"
					field="name"
					@select="option => (selectedBrand = option)"
					class="brandAutocomplete"
					:disabled="products.length > 0"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</div>

			<div v-if="selectedBrand">
				<span class="label">BD CODE</span>
				<div class="addDetails">
					<b-autocomplete
						:data="searchData"
						placeholder="BD Code"
						field="bd_code"
						@typing="searchProducts"
						@select="option => (product = option)"
						class="brandAutocomplete"
					>
						<template slot="empty">No results found</template>
					</b-autocomplete>
				</div>
			</div>

			<div v-if="product">
				<span class="label">ADD</span>
				<b-button type="is-info" icon-left="plus-circle-outline" @click="submit">ADD PRODUCT</b-button>
			</div>
		</div>

		<div class="form" v-if="suppliers.length > 0">
			<div>
				<span class="label">ORDER DATE</span>
				<b-datepicker :date-formatter="dateFormatter" v-model="orderDate" icon="calendar-today" placeholder="Order Date"> </b-datepicker>
			</div>

			<div>
				<span class="label">EXPECTED DATE</span>
				<b-datepicker :date-formatter="dateFormatter" v-model="expectedDate" icon="calendar-today" placeholder="Expected Date"> </b-datepicker>
			</div>
		</div>

		<div class="list">
			<div class="products-list header">
				<div>
					BD CODE
				</div>

				<div>
					BARCODE
				</div>

				<div>
					NAME
				</div>

				<div>
					PACKAGING
				</div>

				<div>
					QUANTITY
				</div>
			</div>

			<div v-for="(item, index) of products" v-bind:key="index" class="products-list">
				<div>
					{{ item.bd_code }}
				</div>

				<div>
					{{ item.barcode }}
				</div>

				<div>
					{{ item.name }}
				</div>

				<div>
					<b-select v-model="item.packaging_selection" disabled>
						<option :value="item.packaging_selection">
							{{ item.original_price_unit || 'single_item' }}
						</option>
					</b-select>
				</div>

				<div>
					<input name="qtyInput" class="qtyInput" type="number" v-on:change="update(index, 'qty', $event.target.value)" />
				</div>
			</div>
		</div>

		<div v-if="products.length > 0" class="detail-buttons-wrapper">
			<div></div>
			<div class="align-right">
				<button class="big-button" @click="save()">SAVE</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'CreatePurchaseOrder',
	data: function() {
		return {
			warehouse: null,
			suppliers: [], // List of suppliers from API
			selectedSupplier: null, // Selected supplier
			supplierSuggestions: '', // Supplier autocomplete suggestions
			brands: [], // List of brands from API
			selectedBrand: null, // Selected brand
			bdcodeData: [], // API bdcode data
			searchData: [], // API Search data
			brandSuggestions: '', // List of brands
			product: null, // 1 selected product
			products: [], // Selected products
			name: null,
			orderDate: null, // Global order date
			expectedDate: null // Global expected date
		}
	},
	computed: {
		filteredSuppliers() {
			return this.suppliers.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.supplierSuggestions.toLowerCase()) >= 0
				)
			})
		},
		filteredBrands() {
			return this.brands.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.brandSuggestions.toLowerCase()) >= 0
				)
			})
		}
	},

	watch: {
		product: function(newData) {
			console.log(newData)
		},

		selectedSupplier: function(newData) {
			this.selectedBrand = null
			this.product = null
			this.products = []
			this.brands = []

			if (newData) {
				// Load brands for selected supplier
				this.brands = newData.brand || []
			}
		},

		selectedBrand: function(newData) {
			this.product = null
			this.products = []
		}
	},

	mounted: async function() {
		// Get current warehouse info
		const warehouseData = {
			populate: ['suppliers']
		}
		const apiGetWarehouse = await this.$api.getWarehouse({ data: warehouseData })
		this.warehouse = apiGetWarehouse.data

		// Get list of suppliers with brands populated
		const supplierData = {
			populate: ['brand']
		}
		const apiGetSuppliers = await this.$api.getSuppliers({ data: supplierData })
		this.suppliers = apiGetSuppliers.data.list || []
	},

	methods: {
		save: async function() {
			// Validate required fields
			if (!this.orderDate) {
				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Order Date is required'
				})
				return
			}

			if (!this.expectedDate) {
				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Expected Date is required'
				})
				return
			}

			var newData = []

			this.products.map(product => {
				var productData = {}
				productData.product = product._id
				productData.order_date = this.$moment(this.orderDate).format('DD/MM/YYYY')
				productData.expected_date = this.$moment(this.expectedDate).format('DD/MM/YYYY')
				// productData.supplier = this.selectedSupplier._id
				productData.currency = 'MMK'
				productData.qty = product.qty

				// Add packaging selection (original_price_unit or default to single_item)
				productData.packaging_selection = product.packaging_selection || 'single_item'

				// Add price and amount
				// productData.price = this.getItemPrice(product)
				// productData.amount = this.getItemAmount(product)

				newData.push(productData)
			})

			const data = {
				supplier: this.selectedSupplier._id,
				brand: this.selectedBrand._id,
				warehouse: this.warehouse._id,
				purchase_order_items: newData,
				type: 'warehouse_to_supplier'
			}

			const apiCreatePO = await this.$api.createPurchaseOrder({ data })

			if (apiCreatePO !== undefined) {
				this.$swal.fire(apiCreatePO.data.purchase_order.code)

				this.$router.push('/po')
			}
		},

		dateFormatter(dt) {
			return dt.toLocaleDateString('en-GB')
		},

		submit: async function() {
			// Initialize product properties
			this.product.qty = null

			// Auto-select packaging based on product.original_price_unit or default to single_item
			this.product.packaging_selection = this.product.original_price_unit || 'single_item'

			this.products.push(this.product)

			// Reset form after adding product
			this.product = null
			this.searchData = []
		},

		update: function(index, item, value) {
			console.log(index, item, value)

			if (item == 'qty') {
				this.products[index].qty = Number(value)
			}
		},

		// Autocomplete search
		searchProducts: async function(name) {
			if (name.length > 1) {
				const data = {
					brand: this.selectedBrand._id,
					bd_code: name
				}

				const apiSearchProduct = await this.$api.searchProduct({ data })

				this.searchData = apiSearchProduct.data.list

				// If no data found, try to search with barcode
				if (apiSearchProduct.data.list && apiSearchProduct.data.list.length < 1) {
					delete data.bd_code

					data.barcode = name

					const apiSearchProduct = await this.$api.searchProduct({ data })

					this.searchData = apiSearchProduct.data.list
				}
			}
		},

		// Get item price (original_price if available, otherwise price)
		getItemPrice: function(item) {
			const price = item.original_price || item.price || 0
			return parseFloat(price).toFixed(2)
		},

		// Get item amount (price * quantity)
		getItemAmount: function(item) {
			const price = item.original_price || item.price || 0
			const quantity = item.qty || 0
			const amount = parseFloat(price) * parseInt(quantity)
			return amount.toFixed(2)
		}
	}
}
</script>

<style scoped>
@import '../styles/AddPurchaseOrder.css';
</style>
